import { DeleteOutlined, EditOutlined, PlusOutlined, FilterOutlined } from '@ant-design/icons';
import {
  Button,
  Dropdown,
  Flex,
  Input,
  Modal,
  Space,
  Table,
  Select,
  Image,
  Typography,
  Switch,
  InputNumber,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';

import new_category_Api from '../../api/new-category';
import newApi from '../../api/new';
import SH_Toolbar from '../../components/SH_Toolbar';
import TV_news_detail from './TV_news_detail';
import projectApi from '../../api/project';

const { Title } = Typography;

const SH_news = () => {
  const [dictionary, setDictionary] = useState({ categories: [], projects: [] });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const refDetail = useRef();

  const [rowLoadingMap, setRowLoadingMap] = useState({}); // { [id]: boolean }
  const highlightTimerRef = useRef({}); // { [id]: timeoutId }

  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: '',
    projectId: undefined,
    categories: undefined,

    isHighlight: undefined, // undefined | true | false
    highlightOrderSort: undefined, // undefined | 1 | -1
  });

  useEffect(() => {
    getProducts(filter);
    getCategory().then((r) => r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      Object.values(highlightTimerRef.current).forEach((t) => clearTimeout(t));
      highlightTimerRef.current = {};
    };
  }, []);

  const getCategory = async () => {
    setLoading(true);
    try {
      const [categories, projects] = await Promise.all([
        new_category_Api.get({ page: 1, limit: 9999 }),
        projectApi.get({ page: 1, limit: 9999 }),
      ]);

      setDictionary({
        categories: categories?.data?.data?.items?.map((x) => ({ ...x, label: x?.names?.vi })) || [],
        projects: projects?.data?.data?.items?.map((x) => ({ ...x, label: x?.names?.vi })) || [],
      });
    } finally {
      setLoading(false);
    }
  };

  const getProducts = (_filter = filter) => {
    setLoading(true);

    const payload = {
      ..._filter,
      limit: _filter?.limit || 10,
      page: _filter?.page || 1,
    };

    newApi
      .get(payload)
      .then((res) => {
        setProducts(res?.data?.data?.items || []);
        setTotal(res?.data?.data?.pagination?.total || 0);
      })
      .finally(() => setLoading(false));
  };

  const applyFilter = (patch) => {
    const next = {
      ...filter,
      ...patch,
      page: patch?.page ?? 1,
    };
    setFilter(next);
    getProducts(next);
  };

  const conFirm = (event) => {
    return Modal.confirm({
      title: 'Xác nhận xóa',
      content: 'Bạn có chắc chắn muốn xóa bản ghi này không ? ',
      onOk: () => {
        deleteItem(event);
      },
      footer: (_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          <OkBtn />
        </>
      ),
      okText: 'Xóa',
      cancelText: 'Hủy',
      okButtonProps: { danger: true },
    });
  };

  const deleteItem = (event) => {
    setLoading(true);
    newApi.delete(event._id).then(() => {
      setLoading(false);
      getProducts(filter);
    });
  };

  const showModal = () => {
    refDetail.current.create();
  };

  const handlePageChange = (page, pageSize) => {
    const next = { ...filter, page, limit: pageSize };
    setFilter(next);
    getProducts(next);
  };

  const setRowLoading = (id, val) => {
    setRowLoadingMap((prev) => ({ ...prev, [id]: val }));
  };

  const handleToggleHighlight = async (record, checked) => {
    const id = record._id;
    setRowLoading(id, true);
    try {
      await newApi.highlight(id, {
        isHighlight: checked,
        highlightOrder: record.highlightOrder || 0,
      });
      getProducts(filter);
    } finally {
      setRowLoading(id, false);
    }
  };

  const updateLocalHighlightOrder = (id, val) => {
    setProducts((prev) => prev.map((x) => (x._id === id ? { ...x, highlightOrder: val } : x)));
  };

  const scheduleUpdateHighlightOrder = (record, val) => {
    const id = record._id;
    const v = val ?? 0;

    updateLocalHighlightOrder(id, v);

    // debounce API
    if (highlightTimerRef.current[id]) clearTimeout(highlightTimerRef.current[id]);

    highlightTimerRef.current[id] = setTimeout(async () => {
      setRowLoading(id, true);
      try {
        await newApi.highlight(id, { isHighlight: true, highlightOrder: v });
      } finally {
        setRowLoading(id, false);
      }
    }, 300);
  };

  const columns = [
    {
      title: 'Tên tin tức (VI)',
      dataIndex: 'names',
      key: 'names_vi',
      ellipsis: true,
      render: (text) => <span>{text?.vi}</span>,
    },
    {
      title: 'Tên tin tức (EN)',
      dataIndex: 'names',
      key: 'names_en',
      ellipsis: true,
      render: (text) => <span>{text?.en}</span>,
    },
    {
      title: 'Loại tin tức',
      dataIndex: 'categoryList',
      key: 'categoryList',
      ellipsis: true,
      render: (categoryList) => categoryList?.map((x) => x?.names?.vi)?.join(', '),
    },
    {
      title: 'Ảnh',
      dataIndex: 'images',
      key: 'images',
      width: 120,
      render: (images) =>
        images?.url ? (
          <Image width={90} height={60} style={{ objectFit: 'cover', borderRadius: 10 }} src={images.url} alt="" />
        ) : (
          <span>-</span>
        ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAtDate',
      key: 'createdAtDate',
      width: 190,
      render: (createdAtDate) => (
        <span>{createdAtDate ? dayjs(createdAtDate).format('HH:mm A DD/MM/YYYY') : ''}</span>
      ),
      sorter: (a, b) => dayjs(a.createdAtDate).valueOf() - dayjs(b.createdAtDate).valueOf(),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Ghim',
      dataIndex: 'isHighlight',
      key: 'isHighlight',
      width: 110,
      render: (_, record) => (
        <Switch
          checked={!!record.isHighlight}
          loading={!!rowLoadingMap[record._id]}
          onChange={(checked) => handleToggleHighlight(record, checked)}
        />
      ),
    },
    {
      title: 'Thứ tự',
      dataIndex: 'highlightOrder',
      key: 'highlightOrder',
      width: 140,
      render: (_, record) => (
        <InputNumber
          min={0}
          controls
          style={{ width: 110 }}
          value={record.highlightOrder ?? 0}
          disabled={!record.isHighlight || !!rowLoadingMap[record._id]}
          onChange={(val) => scheduleUpdateHighlightOrder(record, val)}
          onStep={(val) => scheduleUpdateHighlightOrder(record, val)} // optional
        />
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 190,
      render: (_, record) => (
        <Space size="middle">
          <Button onClick={() => refDetail.current.update(record)} icon={<EditOutlined />}>
            Sửa
          </Button>
          <Button onClick={() => conFirm(record)} type="dashed" danger icon={<DeleteOutlined />}>
            Xoá
          </Button>
        </Space>
      ),
    },
  ];

  const resetFilter = () => {
    const next = {
      page: 1,
      limit: filter.limit || 10,
      search: '',
      projectId: undefined,
      categories: undefined,
      isHighlight: undefined,
      highlightOrderSort: undefined,
    };
    setFilter(next);
    getProducts(next);
  };

  return (
    <>
      <Flex align="center" justify="space-between" wrap="wrap" gap={12} style={{ marginBottom: 12 }}>
        <Title level={2} style={{ margin: 0 }}>
          Tin tức
        </Title>
      </Flex>

      <SH_Toolbar
        left={() => (
          <Button icon={<PlusOutlined />} type="primary" onClick={showModal}>
            Thêm tin tức
          </Button>
        )}
        right={() => (
          <Flex gap={8} wrap="wrap" align="center">
            <Input.Search
              allowClear
              style={{ width: 260 }}
              placeholder="Nhập tên tin tức"
              value={filter.search}
              onChange={(e) => applyFilter({ search: e.target.value })}
            />

            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              dropdownRender={() => (
                <div style={{ width: 340, background: '#fff', padding: 12, borderRadius: 12 }}>
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Dự án</div>
                      <Select
                        allowClear
                        style={{ width: '100%' }}
                        value={filter.projectId}
                        fieldNames={{ label: 'label', value: '_id' }}
                        options={dictionary?.projects}
                        placeholder="Chọn dự án"
                        onChange={(e) => applyFilter({ projectId: e })}
                      />
                    </div>

                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Loại tin tức</div>
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        fieldNames={{ label: 'label', value: '_id' }}
                        value={filter.categories}
                        options={dictionary?.categories}
                        placeholder="Chọn loại tin tức"
                        onChange={(e) => applyFilter({ categories: e })}
                      />
                    </div>

                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Trạng thái ghim</div>
                      <Select
                        allowClear
                        style={{ width: '100%' }}
                        value={filter.isHighlight}
                        placeholder="Tất cả"
                        options={[
                          { label: 'Đang ghim', value: true },
                          { label: 'Không ghim', value: false },
                        ]}
                        onChange={(e) => applyFilter({ isHighlight: e })}
                      />
                    </div>

                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Sắp xếp thứ tự ghim</div>
                      <Select
                        allowClear
                        style={{ width: '100%' }}
                        value={filter.highlightOrderSort}
                        placeholder="Mặc định"
                        options={[
                          { label: 'Thứ tự tăng dần', value: 1 },
                          { label: 'Thứ tự giảm dần', value: -1 },
                        ]}
                        onChange={(e) => applyFilter({ highlightOrderSort: e })}
                      />
                    </div>

                    <Flex justify="flex-end" gap={8}>
                      <Button onClick={resetFilter}>Reset</Button>
                      <Button type="primary" onClick={() => getProducts(filter)}>
                        Áp dụng
                      </Button>
                    </Flex>
                  </Space>
                </div>
              )}
            >
              <Button icon={<FilterOutlined />}>Lọc</Button>
            </Dropdown>
          </Flex>
        )}
      />

      <Table
        rowKey={(record) => record?._id}
        pagination={{
          current: filter.page,
          pageSize: filter.limit,
          total: total,
          onChange: handlePageChange,
          showSizeChanger: true,
        }}
        className="mt-2"
        loading={loading}
        columns={columns}
        dataSource={products}
        scroll={{ x: 1300 }}
      />

      <TV_news_detail
        ref={refDetail}
        getProducts={() => getProducts(filter)}
        categories={dictionary?.categories}
        projects={dictionary?.projects}
      />
    </>
  );
};

export default SH_news;
